import { FC, useMemo } from "react";
import styled from "@emotion/styled";
import { MDSText, MDSTextVariant } from "@/mds";

export interface RecordingLevelMeterProps {
  level: number;
  label?: string;
}

const LevelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LevelTrack = styled.div`
  width: 140px;
  height: 6px;
  border-radius: ${({ theme }) => theme.borderRadius.maximum};
  background-color: ${({ theme }) => theme.colors.grey.x200};
  overflow: hidden;
  position: relative;
`;

const LevelFill = styled.div<{ $percentage: number }>`
  width: ${({ $percentage }) => `${$percentage}%`};
  height: 100%;
  border-radius: inherit;
  background-color: ${({ theme }) => theme.colors.primary.x500};
  transition: width 0.1s ease-out;
`;

export const RecordingLevelMeter: FC<RecordingLevelMeterProps> = ({ level, label = "Level" }) => {
  const { percentage, meterValue, displayPercent } = useMemo(() => {
    const safeLevel = Number.isFinite(level) && level > 0 ? Math.min(level, 1) : 0;
    const percentValue = safeLevel * 100;
    return {
      percentage: percentValue,
      meterValue: safeLevel,
      displayPercent: Math.round(percentValue),
    };
  }, [level]);

  return (
    <LevelContainer>
      <MDSText variant={MDSTextVariant.TextSm}>{label}</MDSText>
      <LevelTrack role="meter" aria-valuemin={0} aria-valuemax={1} aria-valuenow={meterValue}>
        <LevelFill $percentage={percentage} />
      </LevelTrack>
      <MDSText variant={MDSTextVariant.TextXs}>{displayPercent}%</MDSText>
    </LevelContainer>
  );
};

export default RecordingLevelMeter;
